import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, AlertCircle, Globe, Mail, Crown, Monitor, Server, Radar, Loader2, Terminal } from 'lucide-react';
import { useAddWatchlistItemMutation } from '@/lib/services/watchlistApi';
import {
    WatchlistAddRequest,
    WatchlistEntryType,
    WATCHLIST_ENTRY_TYPE_OPTIONS
} from '@/lib/api/types';
import { Input } from '@/components/common/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/ui/Select';

interface AddWatchlistModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const getEntryTypeIcon = (type: string) => {
    switch (type) {
        case 'domain': return <Globe className="h-3.5 w-3.5" />;
        case 'email': return <Mail className="h-3.5 w-3.5" />;
        case 'vip': return <Crown className="h-3.5 w-3.5" />;
        case 'computername': return <Monitor className="h-3.5 w-3.5" />;
        case 'ip': return <Server className="h-3.5 w-3.5" />;
        default: return <Globe className="h-3.5 w-3.5" />;
    }
};

const getEntryPlaceholder = (type: WatchlistEntryType | '') => {
    switch (type) {
        case 'domain': return 'e.g., example.com';
        case 'email': return 'e.g., target@example.com';
        case 'vip': return 'e.g., john.doe';
        case 'computername': return 'e.g., DESKTOP-ABC123';
        case 'ip': return 'e.g., 192.168.1.1';
        default: return 'Enter the value to monitor...';
    }
};

const validateEntry = (entryType: WatchlistEntryType, entry: string): string | null => {
    if (!entry.trim()) {
        return 'Entry is required';
    }

    switch (entryType) {
        case 'email':
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(entry)) {
                return 'Please enter a valid email address';
            }
            break;
        case 'domain':
            const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
            if (!domainRegex.test(entry)) {
                return 'Please enter a valid domain name';
            }
            break;
        case 'ip':
            const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
            if (!ipRegex.test(entry)) {
                return 'Please enter a valid IP address';
            }
            break;
        case 'computername':
            if (entry.length < 1 || entry.length > 15) {
                return 'Computer name must be 1-15 characters';
            }
            break;
        case 'vip':
            if (entry.length < 2) {
                return 'VIP identifier must be at least 2 characters';
            }
            break;
    }

    return null;
};

interface AlertPreferences {
    include_usernames: boolean;
    include_passwords: boolean;
    consumer_alerts: boolean;
    corporate_alerts: boolean;
}

const AddWatchlistModal: React.FC<AddWatchlistModalProps> = ({
    isOpen,
    onClose,
    onSuccess
}) => {
    const [addWatchlistItem, { isLoading, error }] = useAddWatchlistItemMutation();

    // Form state
    const [formData, setFormData] = useState<WatchlistAddRequest>({
        entry_type: 'domain',
        entry: '',
    });

    const [alertPreferences, setAlertPreferences] = useState<AlertPreferences>({
        include_usernames: true,
        include_passwords: true,
        consumer_alerts: true,
        corporate_alerts: true
    });

    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            setFormData({
                entry_type: 'domain',
                entry: '',
            });
            setAlertPreferences({
                include_usernames: true,
                include_passwords: true,
                consumer_alerts: true,
                corporate_alerts: true
            });
            setValidationErrors({});
            setShowAdvancedOptions(false);

            // Lock body scroll
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    // Close modal on escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };

        if (isOpen) document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    const handleInputChange = (field: keyof WatchlistAddRequest) => (
        value: string | WatchlistEntryType
    ) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        // Clear validation error for this field
        if (validationErrors[field]) {
            setValidationErrors(prev => {
                const { [field]: _, ...rest } = prev;
                return rest;
            });
        }

        // Clear entry validation when entry type changes
        if (field === 'entry_type') {
            setFormData(prev => ({ ...prev, entry: '' }));
            if (validationErrors.entry) {
                setValidationErrors(prev => {
                    const { entry: _, ...rest } = prev;
                    return rest;
                });
            }
        }
    };

    const handleAlertPreferenceChange = (key: keyof AlertPreferences) => {
        setAlertPreferences(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        // Validate entry type
        if (!formData.entry_type) errors.entry_type = 'Entry type is required';

        // Validate entry
        const entryError = validateEntry(formData.entry_type, formData.entry);
        if (entryError) errors.entry = entryError;

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        try {
            // Merge form data with alert preferences
            const submitData: WatchlistAddRequest = {
                ...formData,
                ...alertPreferences
            };

            const result = await addWatchlistItem(submitData).unwrap();
            if (result.success) {
                onSuccess();
            }
        } catch (err) {
            console.error('Failed to add watchlist item:', err);
        }
    };

    if (!isOpen) return null;

    const modalContent = (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            {/* Backdrop click handler */}
            <div className="absolute inset-0" onClick={onClose} />

            {/* Modal */}
            <div className="relative w-full max-w-lg max-h-[90vh] bg-[#0A0B0E] border border-[#1A1D24] rounded-xl shadow-[0_0_50px_rgba(0,0,0,0.8)] flex flex-col overflow-hidden animate-fade-in font-mono">

                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-[#1A1D24] bg-[#12151C] flex-shrink-0">
                    <div className="flex items-center gap-4">
                        <h2 className="text-[14px] font-bold text-white tracking-widest uppercase flex items-center gap-2">
                            <Radar className="w-5 h-5 text-[#4da760]" />
                            Add to Watchlist
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-500 hover:text-[#FF3333] hover:bg-[#161A23] rounded-lg transition-all ml-2 border border-transparent hover:border-[#1A1D24]"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Form */}
                <div className="flex-1 overflow-y-auto custom-scrollbar cyber-form-override">
                    <form onSubmit={handleSubmit} className="p-6 space-y-6">

                        {/* Entry Type */}
                        <div className="space-y-2">
                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                Target Class *
                            </label>
                            <Select
                                value={formData.entry_type}
                                onValueChange={(value) => handleInputChange('entry_type')(value as WatchlistEntryType)}
                            >
                                <SelectTrigger className={`bg-[#0A0B0E] border-[#1A1D24] text-slate-300 h-10 text-[12px] focus:ring-0 focus:border-[#4da760] ${validationErrors.entry_type ? 'border-[#FF3333]' : ''}`}>
                                    <SelectValue placeholder="Select target classification" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0A0B0E] border-[#1A1D24] text-slate-300">
                                    {WATCHLIST_ENTRY_TYPE_OPTIONS.map(option => (
                                        <SelectItem key={option.value} value={option.value} className="focus:bg-[#161A23] focus:text-[#4da760] cursor-pointer text-[12px]">
                                            <div className="flex items-center gap-2 uppercase tracking-wider">
                                                {getEntryTypeIcon(option.value)}
                                                {option.label}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                            {/* FIX: Replaced && with ternary for strict TS rendering */}
                            {validationErrors.entry_type ? (
                                <p className="text-[#FF3333] text-[10px] tracking-wide mt-1">{validationErrors.entry_type}</p>
                            ) : null}
                        </div>

                        {/* Entry Input */}
                        <div className="space-y-2">
                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                Target Identifier *
                            </label>
                            <Input
                                type="text"
                                value={formData.entry}
                                onChange={(e) => handleInputChange('entry')(e.target.value)}
                                placeholder={getEntryPlaceholder(formData.entry_type)}
                                error={validationErrors.entry}
                                leftIcon={getEntryTypeIcon(formData.entry_type)}
                                className="bg-[#0A0B0E] border-[#1A1D24] text-[#00FFFF] focus:border-[#4da760] h-10 text-[12px] w-full"
                            />
                        </div>

                        {/* Alert Preferences Section */}
                        <div className="space-y-4 pt-2">
                            <div className="flex items-center justify-between pb-2 border-b border-[#1A1D24]">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                    <Terminal className="w-3.5 h-3.5 text-orange-500" /> Alert Thresholds
                                </label>
                                <button
                                    type="button"
                                    onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                                    className="text-[10px] font-bold uppercase tracking-widest text-[#4da760] hover:text-[#D8B4FE] transition-colors"
                                >
                                    {showAdvancedOptions ? 'Minimize Parameters' : 'Expand Parameters'}
                                </button>
                            </div>

                            {showAdvancedOptions ? (
                                <div className="bg-[#050505] rounded-lg p-5 space-y-5 border border-[#1A1D24] animate-fade-in">

                                    {/* Include Usernames */}
                                    <label className="flex items-start gap-4 cursor-pointer group">
                                        <input
                                            type="checkbox"
                                            checked={alertPreferences.include_usernames}
                                            onChange={() => handleAlertPreferenceChange('include_usernames')}
                                            className="cyber-checkbox mt-0.5"
                                        />
                                        <div>
                                            <div className="text-[11px] font-bold text-slate-300 uppercase tracking-widest group-hover:text-white transition-colors">
                                                Username Intercepts
                                            </div>
                                            <div className="text-[10px] text-slate-500 tracking-wide mt-1">
                                                Trigger alerts for exposed user credentials matching target
                                            </div>
                                        </div>
                                    </label>

                                    {/* Include Passwords */}
                                    <label className="flex items-start gap-4 cursor-pointer group">
                                        <input
                                            type="checkbox"
                                            checked={alertPreferences.include_passwords}
                                            onChange={() => handleAlertPreferenceChange('include_passwords')}
                                            className="cyber-checkbox mt-0.5"
                                        />
                                        <div>
                                            <div className="text-[11px] font-bold text-slate-300 uppercase tracking-widest group-hover:text-white transition-colors">
                                                Password Intercepts
                                            </div>
                                            <div className="text-[10px] text-slate-500 tracking-wide mt-1">
                                                Include decrypted payload data in alert payload
                                            </div>
                                        </div>
                                    </label>

                                    {/* Consumer Alerts */}
                                    <label className="flex items-start gap-4 cursor-pointer group">
                                        <input
                                            type="checkbox"
                                            checked={alertPreferences.consumer_alerts}
                                            onChange={() => handleAlertPreferenceChange('consumer_alerts')}
                                            className="cyber-checkbox mt-0.5"
                                        />
                                        <div>
                                            <div className="text-[11px] font-bold text-slate-300 uppercase tracking-widest group-hover:text-white transition-colors">
                                                Consumer Space Data
                                            </div>
                                            <div className="text-[10px] text-slate-500 tracking-wide mt-1">
                                                Monitor public & consumer database breaches
                                            </div>
                                        </div>
                                    </label>

                                    {/* Corporate Alerts */}
                                    <label className="flex items-start gap-4 cursor-pointer group">
                                        <input
                                            type="checkbox"
                                            checked={alertPreferences.corporate_alerts}
                                            onChange={() => handleAlertPreferenceChange('corporate_alerts')}
                                            className="cyber-checkbox mt-0.5"
                                        />
                                        <div>
                                            <div className="text-[11px] font-bold text-slate-300 uppercase tracking-widest group-hover:text-white transition-colors">
                                                Corporate Space Data
                                            </div>
                                            <div className="text-[10px] text-slate-500 tracking-wide mt-1">
                                                Monitor restricted corporate network breaches
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            ) : (
                                <div className="text-[10px] text-slate-600 uppercase tracking-widest">
                                    [SYS_MSG] Default configuration: All alert parameters active
                                </div>
                            )}
                        </div>

                        {/* Error Display - FIX: Using safe ternary check for unknown object types */}
                        {error ? (
                            <div className="flex items-center gap-2 p-3 bg-[#FF3333]/10 border border-[#FF3333]/30 rounded-lg animate-fade-in">
                                <AlertCircle className="h-4 w-4 text-[#FF3333] flex-shrink-0" />
                                <span className="text-[#FF3333] text-[11px] tracking-wide">
                                    [ERROR] {typeof error === 'object' && error !== null && 'data' in error
                                        ? (error as any).data?.message || 'Failed to establish watch protocol.'
                                        : 'Failed to establish watch protocol. Verify parameters.'
                                    }
                                </span>
                            </div>
                        ) : null}
                    </form>
                </div>

                {/* Actions Footer */}
                <div className="flex justify-end gap-3 p-5 bg-[#12151C] border-t border-[#1A1D24] flex-shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                        className="px-5 py-2.5 text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-white transition-colors disabled:opacity-50"
                    >
                        Abort
                    </button>
                    <button
                        type="button" // Use click via button to ensure pure styling
                        onClick={handleSubmit}
                        disabled={isLoading}
                        className="flex items-center gap-2 px-5 py-2.5 text-[10px] font-bold uppercase tracking-widest bg-[#4da760] text-white rounded hover:bg-[#9333EA] transition-colors disabled:opacity-50 shadow-[0_0_15px_rgba(168,85,247,0.2)]"
                    >
                        {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                        {isLoading ? 'Injecting...' : 'Engage Target'}
                    </button>
                </div>
            </div>

            {/* CSS override for inputs & localized checkboxes */}
            <style dangerouslySetInnerHTML={{
                __html: `
                .cyber-form-override input, .cyber-form-override textarea {
                    background-color: #0A0B0E !important;
                    border-color: #1A1D24 !important;
                    color: #00FFFF !important;
                    font-family: monospace !important;
                }
                .cyber-form-override input:focus, .cyber-form-override textarea:focus {
                    border-color: #4da760 !important;
                    box-shadow: 0 0 10px rgba(168,85,247,0.1) !important;
                    outline: none !important;
                }
                .cyber-form-override input::placeholder, .cyber-form-override textarea::placeholder {
                    color: #475569 !important;
                }
                
                /* Localized Cyber Checkbox for Alert Preferences */
                .cyber-checkbox {
                    appearance: none !important;
                    -webkit-appearance: none !important;
                    background-color: #0A0B0E !important;
                    border: 1px solid #1A1D24 !important;
                    width: 16px !important;
                    height: 16px !important;
                    border-radius: 4px !important;
                    position: relative !important;
                    cursor: pointer !important;
                    outline: none !important;
                    transition: all 0.2s ease !important;
                    flex-shrink: 0 !important;
                }
                .cyber-checkbox:hover {
                    border-color: #4da760 !important;
                }
                .cyber-checkbox:checked {
                    background-color: rgba(168, 85, 247, 0.15) !important;
                    border-color: #4da760 !important;
                }
                .cyber-checkbox:checked::after {
                    content: '' !important;
                    position: absolute !important;
                    left: 4px !important;
                    top: 1px !important;
                    width: 5px !important;
                    height: 9px !important;
                    border: solid #4da760 !important;
                    border-width: 0 2px 2px 0 !important;
                    transform: rotate(45deg) !important;
                }
            `}} />
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default AddWatchlistModal;